import axios from 'axios';
import logger from './utils/logger';

// Get backend URL from runtime config, environment variable, or fallback
const getRuntimeBackendUrl = () => {
  // Check if window.RUNTIME_CONFIG exists (from runtime-config.js)
  if (window.RUNTIME_CONFIG && window.RUNTIME_CONFIG.backendUrl) {
    return window.RUNTIME_CONFIG.backendUrl;
  }
  
  // Fallback to environment variable
  return process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
};

const BACKEND_URL = getRuntimeBackendUrl();

// Create a custom instance of axios
const api = axios.create({
  baseURL: BACKEND_URL,
  timeout: 600000, // 10 minute timeout for large file uploads
  withCredentials: true, // Always send cookies with requests
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'  // Help with CORS preflight
  }
});

// Ensure withCredentials is set for all requests
axios.defaults.withCredentials = true;

// API baseURL configured

// Request interceptor for logging and validation
api.interceptors.request.use(
  (config) => {
    // Enhanced request logging with complete URL (only in development)
    logger.apiRequest(config.method, config.url);
    
    // Ensure credentials are always included
    config.withCredentials = true;
    
    // Special handling for file uploads
    if (config.data instanceof FormData) {
      // Don't modify headers for FormData as axios sets the correct Content-Type with boundary
    }
    
    return config;
  },
  (error) => {
    logger.apiError(error, 'Request setup error');
    return Promise.reject(error);
  }
);

// Response interceptor for logging and response normalization
api.interceptors.response.use(
  (response) => {
    // Add more detailed logging for response format validation
    if (typeof response.data !== 'object' || response.data === null) {
      logger.error('❌ INVALID RESPONSE FORMAT: Response data is not an object', {
        dataType: typeof response.data,
        data: response.data,
        url: response.config.url,
        method: response.config.method
      });
    }
    
    // Ensure response has a success field for consistent handling
    if (typeof response.data === 'object' && response.data !== null && response.data.success === undefined) {
      response.data.success = response.status >= 200 && response.status < 300;
    }
    
    return response;
  },
  (error) => {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      logger.error('❌ Response error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        headers: error.response.headers,
        data: error.response.data,
        method: error.config?.method,
        url: error.config?.url
      });
      
      // Ensure error response has a success field
      if (error.response.data && error.response.data.success === undefined) {
        error.response.data.success = false;
      }
    } else if (error.request) {
      // The request was made but no response was received
      logger.error('❌ No response received:', {
        request: error.request,
        method: error.config?.method,
        url: error.config?.url,
        timeout: error.config?.timeout
      });
    } else {
      // Something happened in setting up the request that triggered an Error
      logger.error('❌ Request setup error:', error.message, {
        config: error.config
      });
    }
    
    // Return the error for the component to handle
    return Promise.reject(error);
  }
);

// Response interceptor for handling authentication errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      logger.error('❌ Response error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        url: error.config?.url
      });

      if (error.response.status === 401) {
        // Clear any stored auth data
        localStorage.removeItem('isAdmin');
        // Don't redirect here, let the component handle it
        logger.log('Authentication error detected');
      }
    } else if (error.request) {
      // The request was made but no response was received
      logger.error('❌ No response received:', {
        request: error.request,
        url: error.config?.url
      });
    } else {
      // Something happened in setting up the request that triggered an Error
      logger.error('❌ Request setup error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Helper function to process fetch API responses for consistency
api.processResponse = async (response) => {
  try {
    // Check if response is empty
    const text = await response.text();
    if (!text || text.trim() === '') {
      logger.error('Empty response received from server');
      return {
        success: false,
        message: 'Empty response received from server',
        error: 'Empty response'
      };
    }
    
    // Try to parse as JSON
    try {
      const data = JSON.parse(text);
      
      // Ensure response has a success field for consistent handling
      if (data.success === undefined) {
        data.success = response.ok;
      }
      
      return data;
    } catch (jsonError) {
      logger.error('Error parsing API response JSON:', jsonError, 'Response text:', text);
      return {
        success: false,
        message: 'Error parsing server response',
        error: jsonError.message,
        responseText: text.substring(0, 100) // Include part of response for debugging
      };
    }
  } catch (error) {
    // If we can't process the response at all
    logger.error('Fatal error processing API response:', error);
    return {
      success: false,
      message: 'Fatal error processing server response',
      error: error.message
    };
  }
};

// DNS Check API function
api.checkDomainDNS = async (domain, forceRefresh = false) => {
  try {
    // Ensure the domain is a string
    domain = String(domain || '').trim();
    
    // If no domain provided, return an error
    if (!domain) {
      logger.error('checkDNS - DNS check failed: No domain provided');
      return {
        success: false,
        message: 'No domain provided'
      };
    }
    
    // Use the proper backend URL instead of a relative URL
    let url = `${BACKEND_URL}/api/dns-check`;
    
    // Make the request
    try {
      logger.log(`Making DNS check request for domain: ${domain}${forceRefresh ? ' (forced refresh)' : ''}`);
      logger.log(`Using backend URL: ${url}`);
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include',
        body: JSON.stringify({ domain, force_refresh: forceRefresh })
      });
      
      // Check if response is OK
      if (!response.ok) {
        logger.error(`DNS check response not OK: ${response.status} ${response.statusText}`);
      }
      
      return api.processResponse(response);
    } catch (fetchError) {
      logger.error('Fetch error during DNS check:', fetchError);
      return {
        success: false,
        message: `Network error during DNS check: ${fetchError.message}`,
        error: fetchError
      };
    }
  } catch (error) {
    logger.error('Error checking domain DNS:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

// Perform reverse DNS lookup for an IP address
api.reverseDnsLookup = async (ipAddress) => {
  try {
    // Validate that we have an IP address
    if (!ipAddress) {
      return {
        success: false,
        message: 'No IP address provided'
      };
    }
    
    // Clean and ensure properly formatted IP
    const cleanIp = String(ipAddress).trim();
    
    logger.log(`API: Performing reverse DNS lookup for IP: ${cleanIp}`);
    logger.log(`Using backend URL: ${BACKEND_URL}`);
    
    // Use axios instead of fetch for better error handling
    try {
      // Use the api instance which has the baseURL configured
      // UPDATED: Use POST method with IP in request body instead of GET with query parameters
      const response = await api.post('/api/dns/reverse', { ip: cleanIp }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      // Return axios response data directly
      return response.data;
    } catch (apiError) {
      if (apiError.response) {
        // Got a response but it's an error
        logger.error(`API responded with error status ${apiError.response.status}:`, apiError.response.data);
        
        // If we got a 405 Method Not Allowed, try with a different method
        if (apiError.response.status === 405) {
          logger.log('Received 405 Method Not Allowed, trying alternative method with fetch');
          
          // Try with a fetch call as fallback - use absolute URL with POST method
          const fetchUrl = `${BACKEND_URL}/api/dns/reverse`;
          logger.log(`Using fallback fetch with URL: ${fetchUrl}`);
          
          const fetchResponse = await fetch(fetchUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ ip: cleanIp }),
            credentials: 'include'
          });
          
          // Process the fetch response
          return api.processResponse(fetchResponse);
        }
        
        // Return the error response data for other errors
        return {
          success: false,
          message: apiError.response.data?.message || apiError.message,
          hostname: null
        };
      }
      
      // No response from API
      throw apiError;
    }
  } catch (error) {
    logger.error('Error performing reverse DNS lookup:', error);
    return {
      success: false,
      message: error.message,
      hostname: null
    };
  }
};

// Company URL Search API function
api.searchCompanyUrls = async (params) => {
  try {
    const { company_name, location = '' } = params;
    logger.log(`API: Searching for URLs for ${company_name}${location ? ' in ' + location : ''}`);
    
    const response = await api.post('/api/search-company-urls', { 
      company_name: company_name,
      location: location
    });
    
    if (response.data) {
      logger.log('API: Search company URLs response:', response.data);
      return response.data;
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    logger.error('Company URL Search API Error:', error);
    
    // Make sure we're propagating the full error
    if (error.response && error.response.data) {
      logger.error('Error details:', error.response.data);
      // Add the response data to the error object for better error handling
      error.details = error.response.data;
    }
    
    throw error;
  }
};

// Update Company URLs API function
api.updateCompanyUrls = async (recordId, websiteUrl, linkedinUrl) => {
  logger.log("API updateCompanyUrls");
  try {
    logger.log(`API: Updating company URLs - Record ID: ${recordId}, Website: ${websiteUrl}, LinkedIn: ${linkedinUrl}`);
    
    // Only include URLs that are not null/undefined
    const payload = { 
      record_id: recordId 
    };
    
    if (websiteUrl) {
      payload.website_url = websiteUrl;
    }
    
    if (linkedinUrl) {
      payload.linkedin_url = linkedinUrl;
    }
    
    logger.log("Request payload:", payload);
    
    // Don't make the request if we have no URLs to update
    if (!payload.website_url && !payload.linkedin_url) {
      logger.warn('No URLs provided for update, skipping API call');
      // logger group end;
      return {
        success: false,
        message: 'No URLs provided for update'
      };
    }
    
    // Enable more detailed request logging
    logger.log("Making API request to /api/update-company-urls");
    logger.log("Axios instance baseURL:", api.defaults.baseURL);
    logger.log("Axios instance headers:", api.defaults.headers);
    
    // Try a simplified API call first to test connection
    try {
      const testResponse = await fetch(`${api.defaults.baseURL}/api/check-auth`, {
        credentials: 'include'
      });
      logger.log("Test API call status:", testResponse.status);
      if (testResponse.status !== 200) {
        logger.warn("Test API call returned non-200 status, connection might be problematic");
      }
    } catch (testError) {
      logger.error("Test API call failed:", testError);
    }
    
    // Make the actual request with more detailed error logging
    try {
      logger.log("Starting axios request");
      const response = await api.post('/api/update-company-urls', payload, {
        timeout: 15000,  // 15 second timeout
        headers: {
          'Content-Type': 'application/json',
          'X-Debug-Info': 'true'
        }
      });
      
      logger.log("Response received:", response.status);
      logger.log("Response headers:", response.headers);
      
      if (response.data) {
        logger.log('API: Update company URLs response:', response.data);
        // logger group end;
        return response.data;
      } else {
        logger.error("Empty response data");
        throw new Error('Invalid response format - empty data');
      }
    } catch (requestError) {
      logger.error("Axios request failed:", requestError);
      throw requestError;
    }
  } catch (error) {
    logger.error('Update Company URLs API Error:', error);
    
    // Make sure we're propagating the full error
    if (error.response) {
      logger.error('Error response status:', error.response.status);
      logger.error('Error response headers:', error.response.headers);
      
      if (error.response.data) {
        logger.error('Error details:', error.response.data);
        // Add the response data to the error object for better error handling
        error.details = error.response.data;
      }
    } else if (error.request) {
      logger.error('No response received, request details:', error.request);
    } else {
      logger.error('Error details:', error.message);
    }
    
    // logger group end;
    throw error;
  }
};

// Update Company URLs using direct SQL (Alternative method)
api.updateCompanyUrlsDirect = async (recordId, websiteUrl, linkedinUrl) => {
  logger.log("API updateCompanyUrlsDirect");
  try {
    logger.log(`API: Direct URL update - Record ID: ${recordId}, Website: ${websiteUrl}, LinkedIn: ${linkedinUrl}`);
    
    // Only include URLs that are not null/undefined
    const payload = { 
      record_id: recordId 
    };
    
    if (websiteUrl) {
      payload.website_url = websiteUrl;
    }
    
    if (linkedinUrl) {
      payload.linkedin_url = linkedinUrl;
    }
    
    logger.log("Request payload:", payload);
    
    // Don't make the request if we have no URLs to update
    if (!payload.website_url && !payload.linkedin_url) {
      logger.warn('No URLs provided for update, skipping API call');
      // logger group end;
      return {
        success: false,
        message: 'No URLs provided for update'
      };
    }
    
    logger.log("Making API request to /api/update-company-urls-direct");
    
    // Use fetch API as an alternative to axios for the direct method
    try {
      const response = await fetch(`${api.defaults.baseURL}/api/update-company-urls-direct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Debug-Info': 'true'
        },
        body: JSON.stringify(payload),
        credentials: 'include',
      });
      
      logger.log("Fetch response status:", response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      logger.log("Response data:", data);
      
      // logger group end;
      return data;
    } catch (fetchError) {
      logger.error("Fetch request failed:", fetchError);
      throw fetchError;
    }
  } catch (error) {
    logger.error('Direct URL update error:', error);
    
    // Make sure we're propagating the full error
    if (error.response) {
      logger.error('Error response status:', error.response.status);
      logger.error('Error response headers:', error.response.headers);
      
      if (error.response.data) {
        logger.error('Error details:', error.response.data);
        // Add the response data to the error object for better error handling
        error.details = error.response.data;
      }
    } else if (error.request) {
      logger.error('No response received, request details:', error.request);
    } else {
      logger.error('Error details:', error.message);
    }
    
    // logger group end;
    throw error;
  }
};

// SPF Test API function
api.testSpf = async (domain) => {
  try {
    const response = await api.post('/api/spf-test', { domain });
    return response.data;
  } catch (error) {
    logger.error('SPF Test API Error:', error);
    throw error;
  }
};

// Clear data API function
api.clearData = async (clearFiles = false) => {
  logger.log("API clearData");
  try {
    logger.log(`API: Clearing data records. Also clear files: ${clearFiles}`);
    
    // Make the request with more explicit parameters
    const response = await api.post('/api/admin/clear-data', 
      { clear_files: clearFiles },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }
    );
    
    logger.log('API: Clear data response:', response.data);
    // logger group end;
    return response.data;
  } catch (error) {
    logger.error('Clear Data API Error:', error);
    
    // Log more detailed error information
    if (error.response) {
      logger.error('Error response status:', error.response.status);
      logger.error('Error response data:', error.response.data);
    } else if (error.request) {
      logger.error('No response received, request details:', error.request);
    } else {
      logger.error('Error message:', error.message);
    }
    
    // logger group end;
    throw error;
  }
};

// Sales Outreach API functions
api.createOutreach = async (outreachData) => {
  try {
    logger.log('API: Creating sales outreach record:', outreachData);
    const response = await api.post('/api/sales-outreach', outreachData);
    logger.log('API: Create outreach response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Create Outreach API Error:', error);
    throw error;
  }
};

api.updateOutreach = async (outreachId, outreachData) => {
  try {
    logger.log(`API: Updating sales outreach record ${outreachId}:`, outreachData);
    const response = await api.put(`/api/sales-outreach/${outreachId}`, outreachData);
    logger.log('API: Update outreach response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Update Outreach API Error:', error);
    throw error;
  }
};

api.deleteOutreach = async (outreachId) => {
  try {
    logger.log(`API: Deleting sales outreach record ${outreachId}`);
    const response = await api.delete(`/api/sales-outreach/${outreachId}`);
    logger.log('API: Delete outreach response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Delete Outreach API Error:', error);
    throw error;
  }
};

api.getOutreaches = async (start_date = null, end_date = null, recordId = null, viewMode = 'user_only') => {
  try {
    let url = '/api/sales-outreach';
    let params = {};
    
    if (recordId) {
      params.record_id = recordId;
    }
    
    if (start_date) {
      params.start_date = start_date;
    }
    
    if (end_date) {
      params.end_date = end_date;
    }
    
    // Add view_mode parameter - valid options now include:
    // - 'user_only': Show only the current user's outreaches
    // - 'company_only': Show all users' outreaches for a specific company (admin only)
    // - 'user_company': Show current user's outreaches for a specific company
    params.view_mode = viewMode;
    
    // Convert params object to query string if there are any params
    const queryString = Object.keys(params).length > 0 
      ? '?' + new URLSearchParams(params).toString() 
      : '';
    
    logger.log(`API: Getting sales outreach records${recordId ? ` for record ${recordId}` : ''}${start_date ? ` from ${start_date}` : ''}${end_date ? ` to ${end_date}` : ''} with view_mode=${viewMode}`);
    const response = await api.get(url + queryString);
    logger.log('API: Get outreaches response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Get Outreaches API Error:', error);
    throw error;
  }
};

api.getOutreachStatistics = async (start_date = null, end_date = null) => {
  try {
    let url = '/api/sales-outreach/statistics';
    let params = {};
    
    if (start_date) {
      params.start_date = start_date;
    }
    
    if (end_date) {
      params.end_date = end_date;
    }
    
    // Convert params object to query string if there are any params
    const queryString = Object.keys(params).length > 0 
      ? '?' + new URLSearchParams(params).toString() 
      : '';
    
    logger.log(`API: Getting sales outreach statistics${start_date ? ` from ${start_date}` : ''}${end_date ? ` to ${end_date}` : ''}`);
    const response = await api.get(url + queryString);
    logger.log('API: Get outreach statistics response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Get Outreach Statistics API Error:', error);
    throw error;
  }
};

// New function to get user-specific outreach statistics
api.getUserOutreachStatistics = async (username, start_date = null, end_date = null) => {
  try {
    if (!username) {
      logger.warn('API: No username provided for user outreach statistics');
      return { success: false, message: 'No username provided' };
    }
    
    let url = `/api/sales-outreach/statistics/user/${username}`;
    let params = {};
    
    if (start_date) {
      params.start_date = start_date;
    }
    
    if (end_date) {
      params.end_date = end_date;
    }
    
    // Convert params object to query string if there are any params
    const queryString = Object.keys(params).length > 0 
      ? '?' + new URLSearchParams(params).toString() 
      : '';
    
    logger.log(`API: Getting sales outreach statistics for user ${username}${start_date ? ` from ${start_date}` : ''}${end_date ? ` to ${end_date}` : ''}`);
    const response = await api.get(url + queryString);
    logger.log('API: Get user outreach statistics response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Get User Outreach Statistics API Error:', error);
    throw error;
  }
};

// Diagnostic function to check outreach status for a specific record
api.checkOutreachStatus = async (recordId) => {
  try {
    logger.log(`Making diagnostic API request for outreach status of record ${recordId}`);
    const response = await api.get(`/api/outreach/status/${recordId}`);
    return response.data;
  } catch (error) {
    logger.error('Error checking outreach status:', error);
    throw error;
  }
};

// New function to get today's and upcoming meetings and followups
api.getTodayEvents = async (daysAhead = 7, includeCompleted = false, daysPast = 30) => {
  try {
    logger.log(`API: Getting today and upcoming meetings and followups (include completed: ${includeCompleted}, days past: ${daysPast})`);
    const response = await api.get('/api/sales-outreach/today-events', {
      params: { 
        days_ahead: daysAhead,
        days_past: daysPast,
        include_completed: includeCompleted 
      }
    });
    logger.log('API: Get today events response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Get Today Events API Error:', error);
    throw error;
  }
};

// Add function to fetch unique values for a specific column
api.getColumnValues = async (columnName, limit = 500) => {
  try {
    logger.log(`API: Fetching unique values for column '${columnName}' (limit: ${limit})`);
    const response = await api.get(`/column-values/${encodeURIComponent(columnName)}`, {
      params: { limit }
    });
    
    if (response.data.success) {
      logger.log(`API: Got ${response.data.values.length} unique values for ${columnName} (truncated: ${response.data.truncated})`);
      return response.data;
    } else {
      logger.error('Failed to get column values:', response.data.message);
      throw new Error(response.data.message || 'Failed to fetch column values');
    }
  } catch (error) {
    logger.error(`Error fetching column values for ${columnName}:`, error);
    throw error;
  }
};

// New function to get all users (needed for OutreachStatistics)
api.getUsers = async () => {
  try {
    logger.log('API: Getting all users');
    // We use the /api/users endpoint for admins, and a different endpoint for supervisors
    // as supervisors should only have read access to user data
    const response = await api.get('/api/users');
    logger.log('API: Get users response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Get Users API Error:', error);
    throw error;
  }
};

// Check if URL exists in records API function
api.checkUrlInRecords = async (url) => {
  try {
    logger.log(`API: Checking if URL exists in records: ${url}`);
    
    const response = await api.post('/api/check-url-in-records', { url });
    
    if (response.data) {
      logger.log('API: Check URL in records response:', response.data);
      return response.data;
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    logger.error('Check URL in records API Error:', error);
    
    // Make sure we're propagating the full error
    if (error.response && error.response.data) {
      logger.error('Error details:', error.response.data);
      // Add the response data to the error object for better error handling
      error.details = error.response.data;
    }
    
    throw error;
  }
};

// Add Record API function
api.addRecord = async (recordData) => {
  try {
    logger.log('API: Adding new record:', recordData);
    const response = await api.post('/api/add-record', recordData);
    
    if (response.data) {
      logger.log('API: Add record response:', response.data);
      return response.data;
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    logger.error('Add Record API Error:', error);
    
    // Make sure we're propagating the full error
    if (error.response && error.response.data) {
      logger.error('Error details:', error.response.data);
      // Add the response data to the error object for better error handling
      error.details = error.response.data;
    }
    
    throw error;
  }
};

// Add function to mark follow-up as completed or uncompleted
api.toggleOutreachCompletion = async (outreachId, completed) => {
  try {
    // Validate outreachId is provided and valid
    if (!outreachId) {
      logger.error('Error: No outreach ID provided to toggleOutreachCompletion');
      return { 
        success: false, 
        message: 'No outreach ID provided'
      };
    }
    
    logger.log(`API: Marking outreach ${outreachId} as ${completed ? 'completed' : 'not completed'}`);
    const response = await api.post(`/api/sales-outreach/${outreachId}/complete`, { completed });
    logger.log('API: Toggle outreach completion response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Toggle Outreach Completion API Error:', error);
    throw error;
  }
};

// Add function to update a single field value for a record
api.updateRecordField = async (recordId, fieldName, fieldValue) => {
  logger.log("DEBUG: api.updateRecordField");
  try {
    if (!recordId) {
      logger.error('Error: No record ID provided to updateRecordField');
      // logger group end;
      return { 
        success: false, 
        message: 'No record ID provided'
      };
    }
    
    if (!fieldName) {
      logger.error('Error: No field name provided to updateRecordField');
      // logger group end;
      return { 
        success: false, 
        message: 'No field name provided'
      };
    }
    
    logger.log(`API: Updating field "${fieldName}" for record ${recordId} to value:`, fieldValue);
    
    // Create a payload with the field to update
    const payload = {
      record_id: recordId,
      field_name: fieldName,
      field_value: fieldValue
    };
    
    logger.log("DEBUG: Making API call with payload:", payload);
    const response = await api.post('/api/update-record-field', payload);
    logger.log('DEBUG: Update record field response status:', response.status);
    logger.log('DEBUG: Update record field response data:', response.data);
    // logger group end;
    return response.data;
  } catch (error) {
    logger.error('DEBUG: Update Record Field API Error:', error);
    
    // Make sure we're propagating the full error details
    if (error.response) {
      logger.error('DEBUG: Error response status:', error.response.status);
      logger.error('DEBUG: Error response headers:', error.response.headers);
      
      if (error.response.data) {
        logger.error('DEBUG: Error response data:', error.response.data);
        // Add the response data to the error object for better error handling
        error.details = error.response.data;
      }
    } else if (error.request) {
      logger.error('DEBUG: No response received, request details:', error.request);
    } else {
      logger.error('DEBUG: Error message:', error.message);
      logger.error('DEBUG: Error stack:', error.stack);
    }
    
    // logger group end;
    // Return a formatted error for easier handling in components
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'An unknown error occurred'
    };
  }
};

// Add function to query the Perplexity API
api.perplexityAsk = async (messages) => {
  try {
    logger.log('API: Calling Perplexity API with messages:', messages);
    
    // Add timeout to avoid hanging
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout (increased from 8 seconds)
    
    const response = await fetch(`${BACKEND_URL}/api/perplexity/ask`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ messages }),
      credentials: 'include',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      const errorText = await response.text();
      logger.error(`API: Perplexity API request failed with status ${response.status}: ${errorText}`);
      
      return {
        success: false,
        message: `API request failed with status ${response.status}: ${errorText.substring(0, 100)}`,
        status: response.status
      };
    }
    
    // Handle empty response
    const text = await response.text();
    if (!text.trim()) {
      logger.error('API: Perplexity API returned empty response');
      return {
        success: false,
        message: 'Empty response from API'
      };
    }
    
    try {
      const data = JSON.parse(text);
      logger.log('API: Perplexity API response:', data);
      return data;
    } catch (e) {
      logger.error('API: Failed to parse Perplexity API response:', e);
      logger.error('API: Raw response text:', text);
      return {
        success: false,
        message: `Invalid JSON response: ${e.message}`,
        rawText: text
      };
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      logger.error('API: Perplexity request timed out');
      return {
        success: false,
        message: 'Request timed out after 30 seconds'  // Updated error message to reflect new timeout
      };
    }
    
    logger.error('API: Error calling Perplexity API:', error);
    return {
      success: false,
      message: error.message || 'Unknown error',
      error: error
    };
  }
};

// Get a specific record by ID
api.getRecord = async (recordId) => {
  try {
    if (!recordId) {
      logger.error('Error: No record ID provided to getRecord');
      return { 
        success: false, 
        message: 'No record ID provided'
      };
    }
    
    logger.log(`API: Fetching record with ID ${recordId}`);
    // Use a POST request instead of GET since the backend endpoint expects POST
    const response = await api.post('/api/get-record', { record_id: recordId });
    logger.log('API: Get record response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Get Record API Error:', error);
    
    // Make sure we're propagating the full error details
    if (error.response) {
      logger.error('Error response status:', error.response.status);
      
      if (error.response.data) {
        logger.error('Error response data:', error.response.data);
        // Add the response data to the error object for better error handling
        error.details = error.response.data;
      }
    }
    
    // Return a formatted error for easier handling in components
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to fetch record details'
    };
  }
};

// Add a function to send frontend logs to the backend
export const sendFrontendLogs = async (logs) => {
  try {
    return await api.post('/api/logs/frontend', { logs });
  } catch (error) {
    logger.error('Error sending frontend logs:', error);
    return { success: false, message: error.message };
  }
};

api.getConsoleLog = async () => {
  try {
    logger.log('Getting console log data');
    const response = await api.get('/api/admin/logs/console');
    return response.data;
  } catch (error) {
    logger.error('Get Console Log API Error:', error);
    throw error;
  }
};

// DNS Statistics API Functions
api.getDnsGeneralStats = async () => {
  try {
    logger.log('Getting DNS general statistics');
    const response = await api.get('/api/dns-stats/general');
    return response.data;
  } catch (error) {
    logger.error('Get DNS General Stats API Error:', error);
    throw error;
  }
};

api.getDnsEmailProviderStats = async () => {
  try {
    logger.log('Getting DNS email provider statistics');
    const response = await api.get('/api/dns-stats/email-providers');
    return response.data;
  } catch (error) {
    logger.error('Get DNS Email Provider Stats API Error:', error);
    throw error;
  }
};

api.getDnsDmarcPolicyStats = async () => {
  try {
    logger.log('Getting DNS DMARC policy statistics');
    const response = await api.get('/api/dns-stats/dmarc-policies');
    return response.data;
  } catch (error) {
    logger.error('Get DNS DMARC Policy Stats API Error:', error);
    throw error;
  }
};

api.getDnsSpfProviderStats = async () => {
  try {
    logger.log('Getting DNS SPF provider statistics');
    const response = await api.get('/api/dns-stats/spf-providers');
    return response.data;
  } catch (error) {
    logger.error('Get DNS SPF Provider Stats API Error:', error);
    throw error;
  }
};

api.getDnsDmarcRuaStats = async () => {
  try {
    logger.log('Getting DNS DMARC RUA statistics');
    const response = await api.get('/api/dns-stats/dmarc-rua');
    return response.data;
  } catch (error) {
    logger.error('Get DNS DMARC RUA Stats API Error:', error);
    throw error;
  }
};

api.getDnsMxProviderStats = async () => {
  try {
    logger.log('Getting DNS MX provider statistics');
    const response = await api.get('/api/dns-stats/mx-providers');
    return response.data;
  } catch (error) {
    logger.error('Get DNS MX Provider Stats API Error:', error);
    throw error;
  }
};

api.getDnsVerificationStats = async () => {
  try {
    logger.log('Getting DNS verification service statistics');
    const response = await api.get('/api/dns-stats/verification-services');
    return response.data;
  } catch (error) {
    logger.error('Get DNS Verification Stats API Error:', error);
    throw error;
  }
};

// DNS Bulk Check API Functions
api.getDnsBulkStats = async () => {
  try {
    logger.log('🔍 API: Getting DNS bulk check statistics');
    const response = await api.get('/api/dns-bulk-stats');
    logger.log('🔍 API: DNS bulk stats response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('❌ Get DNS Bulk Stats API Error:', error);
    logger.error('❌ Error details:', error.response?.data || error.message);
    // Return a formatted error instead of throwing
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Error fetching DNS bulk stats',
      data: null
    };
  }
};

api.startDnsBulkCheck = async (maxDomains = 50, batchSize = 10, checkMode = 'new', daysOld = 30) => {
  try {
    logger.log(`🚀 API: Starting DNS bulk check with max domains ${maxDomains}, batch size ${batchSize}, mode ${checkMode}, days old ${daysOld}`);
    
    // Make sure parameters are valid
    if (!maxDomains || isNaN(parseInt(maxDomains))) {
      logger.error('❌ Invalid maxDomains parameter:', maxDomains);
      return {
        success: false,
        message: 'Invalid maxDomains parameter',
        operation_id: null
      };
    }
    
    if (!batchSize || isNaN(parseInt(batchSize))) {
      logger.error('❌ Invalid batchSize parameter:', batchSize);
      return {
        success: false,
        message: 'Invalid batchSize parameter',
        operation_id: null
      };
    }
    
    // Validate new parameters
    if (!['new', 'recheck'].includes(checkMode)) {
      logger.error('❌ Invalid checkMode parameter:', checkMode);
      return {
        success: false,
        message: 'Invalid checkMode parameter (must be "new" or "recheck")',
        operation_id: null
      };
    }
    
    if (checkMode === 'recheck' && (!daysOld || isNaN(parseInt(daysOld)) || daysOld < 1)) {
      logger.error('❌ Invalid daysOld parameter:', daysOld);
      return {
        success: false,
        message: 'Invalid daysOld parameter (must be a positive number)',
        operation_id: null
      };
    }
    
    // Log the actual request we're making
    logger.log('🔍 API: Making POST request to /api/dns-bulk-check with payload:', { 
      max_domains: maxDomains,
      batch_size: batchSize,
      check_mode: checkMode,
      days_old: daysOld
    });
    
    const response = await api.post('/api/dns-bulk-check', { 
      max_domains: maxDomains,
      batch_size: batchSize,
      check_mode: checkMode,
      days_old: daysOld
    });
    
    logger.log('🔍 API: Start DNS bulk check response:', response.data);
    
    return response.data;
  } catch (error) {
    logger.error('❌ Start DNS Bulk Check API Error:', error);
    logger.error('❌ Error details:', error.response?.data || error.message);
    
    // Return a formatted error instead of throwing
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Error starting DNS bulk check',
      operation_id: null
    };
  }
};

api.getDnsBulkCheckStatus = async (operationId) => {
  try {
    // Validate operationId
    if (!operationId) {
      logger.error('❌ API: Missing operation ID for getDnsBulkCheckStatus');
      return {
        success: false,
        message: 'Missing operation ID',
        data: {
          status: 'error',
          error: 'Missing operation ID'
        }
      };
    }
    
    logger.log(`🔍 API: Getting DNS bulk check status for operation ${operationId}`);
    const response = await api.get(`/api/dns-bulk-check/${operationId}`, {
      // Add a timeout to avoid hanging
      timeout: 8000,
    });
    
    // Log the status
    if (response.data && response.data.data) {
      const status = response.data.data.status;
      const progress = response.data.data.percent_complete.toFixed(1);
      logger.log(`✅ API: Operation ${operationId} status: ${status}, progress: ${progress}%`);
    } else {
      logger.warn('⚠️ API: Unexpected response format from status check:', response.data);
    }
    
    return response.data;
  } catch (error) {
    logger.error(`❌ Get DNS Bulk Check Status API Error for operation ${operationId}:`, error);
    logger.error('❌ Error details:', error.response?.data || error.message);
    
    // Return a formatted error response instead of throwing
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Error fetching operation status',
      // If the operation doesn't exist anymore, we'll consider it completed
      // to stop polling
      data: { 
        status: 'error',
        error: error.message
      }
    };
  }
};

api.cancelDnsBulkCheck = async (operationId) => {
  try {
    // Validate operationId
    if (!operationId) {
      logger.error('❌ API: Missing operation ID for cancelDnsBulkCheck');
      return {
        success: false,
        message: 'Missing operation ID'
      };
    }
    
    logger.log(`🔍 API: Cancelling DNS bulk check operation ${operationId}`);
    const response = await api.delete(`/api/dns-bulk-check/${operationId}`);
    logger.log('🔍 API: Cancel DNS bulk check response:', response.data);
    return response.data;
  } catch (error) {
    logger.error(`❌ Cancel DNS Bulk Check API Error for operation ${operationId}:`, error);
    logger.error('❌ Error details:', error.response?.data || error.message);
    
    // Return a formatted error instead of throwing
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Error cancelling DNS bulk check'
    };
  }
};

api.getDnsBulkCheckOperations = async () => {
  try {
    logger.log('🔍 API: Getting all DNS bulk check operations');
    const response = await api.get('/api/dns-bulk-check-operations', {
      // Add a timeout to avoid hanging
      timeout: 8000,
    });
    logger.log('🔍 API: Get DNS bulk check operations response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('❌ Get DNS Bulk Check Operations API Error:', error);
    logger.error('❌ Error details:', error.response?.data || error.message);
    
    // Return a formatted error response instead of throwing
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Error fetching operations',
      data: [] // Return empty array to avoid UI errors
    };
  }
};

// DNS Verification Pattern Management API Functions
api.getDnsVerificationCategories = async () => {
  try {
    logger.log('Getting DNS verification categories');
    const response = await api.get('/api/dns-stats/verification-categories', {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    logger.error('Get DNS Verification Categories API Error:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Error fetching DNS verification categories',
      categories: []
    };
  }
};

api.getDnsVerificationCategory = async (categoryId) => {
  try {
    logger.log(`Getting DNS verification category: ${categoryId}`);
    const response = await api.get(`/api/dns-stats/verification-categories/${categoryId}`, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    logger.error('Get DNS Verification Category API Error:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || `Error fetching DNS verification category ${categoryId}`,
      category: null
    };
  }
};

api.addDnsVerificationCategory = async (categoryData) => {
  try {
    logger.log('Adding DNS verification category:', categoryData);
    const response = await api.post('/api/dns-stats/verification-categories', categoryData, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    logger.error('Add DNS Verification Category API Error:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Error adding DNS verification category',
      category: null
    };
  }
};

api.updateDnsVerificationCategory = async (categoryId, categoryData) => {
  try {
    logger.log(`Updating DNS verification category ${categoryId}:`, categoryData);
    const response = await api.put(`/api/dns-stats/verification-categories/${categoryId}`, categoryData, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    logger.error('Update DNS Verification Category API Error:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || `Error updating DNS verification category ${categoryId}`,
      category: null
    };
  }
};

api.deleteDnsVerificationCategory = async (categoryId) => {
  try {
    logger.log(`Deleting DNS verification category: ${categoryId}`);
    const response = await api.delete(`/api/dns-stats/verification-categories/${categoryId}`, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    logger.error('Delete DNS Verification Category API Error:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || `Error deleting DNS verification category ${categoryId}`,
    };
  }
};

api.getDnsVerificationPatterns = async () => {
  try {
    logger.log('Getting DNS verification patterns');
    const response = await api.get('/api/dns-stats/verification-patterns', {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    logger.error('Get DNS Verification Patterns API Error:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Error fetching DNS verification patterns',
      patterns: []
    };
  }
};

api.getRecentDnsVerificationPatterns = async (days = 7) => {
  try {
    logger.log(`Getting recent DNS verification patterns (${days} days)`);
    const response = await api.get(`/api/dns-stats/verification-patterns/recent?days=${days}`, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    logger.error('Get Recent DNS Verification Patterns API Error:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Error fetching recent DNS verification patterns',
      patterns: []
    };
  }
};

api.getDnsVerificationPattern = async (patternId) => {
  try {
    logger.log(`Getting DNS verification pattern: ${patternId}`);
    const response = await api.get(`/api/dns-stats/verification-patterns/${patternId}`, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    logger.error('Get DNS Verification Pattern API Error:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || `Error fetching DNS verification pattern ${patternId}`,
      pattern: null
    };
  }
};

api.addDnsVerificationPattern = async (patternData) => {
  try {
    logger.log('Adding DNS verification pattern:', patternData);
    const response = await api.post('/api/dns-stats/verification-patterns', patternData, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    logger.error('Add DNS Verification Pattern API Error:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Error adding DNS verification pattern',
      pattern: null
    };
  }
};

api.updateDnsVerificationPattern = async (patternId, patternData) => {
  try {
    logger.log(`Updating DNS verification pattern ${patternId}:`, patternData);
    const response = await api.put(`/api/dns-stats/verification-patterns/${patternId}`, patternData, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    logger.error('Update DNS Verification Pattern API Error:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || `Error updating DNS verification pattern ${patternId}`,
      pattern: null
    };
  }
};

api.deleteDnsVerificationPattern = async (patternId) => {
  try {
    logger.log(`Deleting DNS verification pattern: ${patternId}`);
    const response = await api.delete(`/api/dns-stats/verification-patterns/${patternId}`, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    logger.error('Delete DNS Verification Pattern API Error:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || `Error deleting DNS verification pattern ${patternId}`,
    };
  }
};

api.getRecentlyAddedDnsVerificationPatterns = api.getRecentDnsVerificationPatterns;
logger.log('Added backward compatibility alias for getRecentlyAddedDnsVerificationPatterns');

// Add function to log URL search activity from Header component
api.logUrlSearch = async (companyName, url, domain) => {
  try {
    logger.log(`API: Logging URL search for company: ${companyName}, URL: ${url}`);
    
    const response = await api.post('/api/log-url-search', {
      company_name: companyName,
      url: url,
      domain: domain
    });
    
    logger.log('API: Log URL search response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Log URL Search API Error:', error);
    
    // Make sure we're propagating the full error
    if (error.response && error.response.data) {
      logger.error('Error details:', error.response.data);
      error.details = error.response.data;
    }
    
    throw error;
  }
};

// DNS Change Tracking API functions
api.getDnsChanges = async (params = {}) => {
  try {
    logger.log('🔍 API: Getting DNS changes with params:', params);
    
    // Build query string from parameters
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.per_page) queryParams.append('per_page', params.per_page);
    if (params.domain) queryParams.append('domain', params.domain);
    if (params.change_type) queryParams.append('change_type', params.change_type);
    if (params.change_category) queryParams.append('change_category', params.change_category);
    if (params.significance) queryParams.append('significance', params.significance);
    if (params.days_back) queryParams.append('days_back', params.days_back);
    
    const response = await api.get(`/api/dns-changes?${queryParams.toString()}`);
    logger.log('✅ API: DNS changes retrieved successfully');
    return response.data;
  } catch (error) {
    logger.error('❌ DNS Changes API Error:', error);
    throw error;
  }
};

api.getDnsChangeStats = async (daysBack = 30) => {
  try {
    logger.log(`🔍 API: Getting DNS change stats for last ${daysBack} days`);
    const response = await api.get(`/api/dns-changes/stats?days_back=${daysBack}`);
    logger.log('✅ API: DNS change stats retrieved successfully');
    return response.data;
  } catch (error) {
    logger.error('❌ DNS Change Stats API Error:', error);
    throw error;
  }
};

api.getDomainChangeHistory = async (domain, limit = 50) => {
  try {
    logger.log(`🔍 API: Getting change history for domain: ${domain}`);
    const response = await api.get(`/api/dns-changes/domain/${encodeURIComponent(domain)}?limit=${limit}`);
    logger.log('✅ API: Domain change history retrieved successfully');
    return response.data;
  } catch (error) {
    logger.error('❌ Domain Change History API Error:', error);
    throw error;
  }
};

api.getDomainSecurityChanges = async (domain, days = 7) => {
  try {
    logger.log(`🔍 API: Getting security changes for domain: ${domain} (last ${days} days)`);
    const response = await api.get(`/api/dns-changes/domain/${encodeURIComponent(domain)}/security?days=${days}`);
    logger.log('✅ API: Domain security changes retrieved successfully');
    return response.data;
  } catch (error) {
    logger.error('❌ Domain Security Changes API Error:', error);
    throw error;
  }
};

api.getDnsChangeFilters = async () => {
  try {
    logger.log('🔍 API: Getting DNS change filter options');
    const response = await api.get('/api/dns-changes/filters');
    logger.log('✅ API: DNS change filters retrieved successfully');
    return response.data;
  } catch (error) {
    logger.error('❌ DNS Change Filters API Error:', error);
    throw error;
  }
};

// DNS Scheduler Status API function
api.getDnsSchedulerStatus = async () => {
  try {
    logger.log('🔍 API: Getting DNS scheduler status');
    const response = await api.get('/api/admin/dns-scheduler-status', {
      withCredentials: true
    });
    logger.log('✅ API: DNS scheduler status retrieved successfully');
    return response.data;
  } catch (error) {
    logger.error('❌ DNS Scheduler Status API Error:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Error fetching DNS scheduler status',
      data: null
    };
  }
};

// User Profile API function
api.updateUserProfile = async (updateData) => {
  try {
    logger.log('🔍 API: Updating user profile with data:', updateData);
    const response = await api.put('/api/user/profile', updateData, {
      withCredentials: true
    });
    logger.log('✅ API: User profile updated successfully');
    return response.data;
  } catch (error) {
    logger.error('❌ Update User Profile API Error:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Error updating user profile',
      user: null
    };
  }
};

api.getMyTeam = async () => {
  try {
    const response = await api.get('/api/teams/me');
    return response.data;
  } catch (error) {
    logger.error('Get My Team API Error:', error);
    return { success: false, team: null, members: [] };
  }
};

api.getTeams = async () => {
  const response = await api.get('/api/teams');
  return response.data;
};

api.getTeamAvailableUsers = async (domain = null) => {
  const query = domain ? `?domain=${encodeURIComponent(domain)}` : '';
  const response = await api.get(`/api/teams/available-users${query}`);
  return response.data;
};

api.createTeam = async (payload) => {
  const response = await api.post('/api/teams', payload);
  return response.data;
};

api.deleteTeam = async (teamId) => {
  const response = await api.delete(`/api/teams/${teamId}`);
  return response.data;
};

api.addTeamMember = async (teamId, userId) => {
  const response = await api.post(`/api/teams/${teamId}/members`, { user_id: userId });
  return response.data;
};

api.removeTeamMember = async (teamId, userId) => {
  const response = await api.delete(`/api/teams/${teamId}/members/${userId}`);
  return response.data;
};

// Get users assigned to the current supervisor (for supervisor role)
api.getAssignedUsers = async () => {
  try {
    logger.log('API: Getting users assigned to current supervisor');
    const response = await api.get('/api/supervisor/assigned-users');
    logger.log('API: Get assigned users response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Get Assigned Users API Error:', error);
    throw error;
  }
};

// Salesforce Integration API Functions
api.salesforceConnect = async (username, password, securityToken = '') => {
  try {
    logger.log('API: Connecting to Salesforce');
    const response = await api.post('/api/salesforce/connect', {
      username,
      password,
      security_token: securityToken
    });
    logger.log('API: Salesforce connect response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Salesforce Connect API Error:', error);
    throw error;
  }
};

api.salesforceDisconnect = async () => {
  try {
    logger.log('API: Disconnecting from Salesforce');
    const response = await api.post('/api/salesforce/disconnect');
    logger.log('API: Salesforce disconnect response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Salesforce Disconnect API Error:', error);
    throw error;
  }
};

api.salesforceStatus = async () => {
  try {
    logger.log('API: Checking Salesforce connection status');
    const response = await api.get('/api/salesforce/status');
    logger.log('API: Salesforce status response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Salesforce Status API Error:', error);
    throw error;
  }
};

api.salesforceGetRecords = async (recordType = 'Lead', limit = 100, offset = 0, filters = {}, search = '', sortField = 'LastModifiedDate', sortOrder = 'DESC') => {
  try {
    logger.log(`API: Fetching Salesforce ${recordType} records`);
    const params = new URLSearchParams({
      record_type: recordType,
      limit: limit.toString(),
      offset: offset.toString(),
      sort_field: sortField,
      sort_order: sortOrder
    });
    
    // Add search if provided
    if (search) {
      params.append('search', search);
    }
    
    // Add filters
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });
    
    const response = await api.get(`/api/salesforce/records?${params.toString()}`);
    logger.log('API: Salesforce records response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Salesforce Get Records API Error:', error);
    throw error;
  }
};

api.salesforceEnrich = async (recordIds, recordType = 'Lead') => {
  try {
    logger.log(`API: Enriching Salesforce ${recordType} records`);
    const response = await api.post('/api/salesforce/enrich', {
      record_ids: recordIds,
      record_type: recordType
    });
    logger.log('API: Salesforce enrich response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Salesforce Enrich API Error:', error);
    throw error;
  }
};

api.salesforceSync = async (recordType, recordId, updateFields) => {
  try {
    logger.log(`API: Syncing enriched data to Salesforce ${recordType} record ${recordId}`);
    const response = await api.post('/api/salesforce/sync', {
      record_type: recordType,
      record_id: recordId,
      update_fields: updateFields
    });
    logger.log('API: Salesforce sync response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Salesforce Sync API Error:', error);
    throw error;
  }
};

api.salesforceBulkEnrich = async (recordIds, recordType = 'Lead') => {
  try {
    logger.log(`API: Bulk enriching ${recordIds.length} Salesforce ${recordType} records`);
    const response = await api.post('/api/salesforce/enrich', {
      record_ids: recordIds,
      record_type: recordType
    });
    logger.log('API: Salesforce bulk enrich response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Salesforce Bulk Enrich API Error:', error);
    throw error;
  }
};

api.salesforceBulkSync = async (recordType, records) => {
  try {
    logger.log(`API: Bulk syncing ${records.length} Salesforce ${recordType} records`);
    const response = await api.post('/api/salesforce/bulk-sync', {
      record_type: recordType,
      records: records
    });
    logger.log('API: Salesforce bulk sync response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Salesforce Bulk Sync API Error:', error);
    throw error;
  }
};

api.salesforceCreateLead = async (leadData, checkDuplicates = true) => {
  try {
    logger.log('API: Creating new Salesforce Lead:', leadData);
    const response = await api.post('/api/salesforce/create-lead', {
      lead_data: leadData,
      check_duplicates: checkDuplicates
    });
    logger.log('API: Salesforce create Lead response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Salesforce Create Lead API Error:', error);
    throw error;
  }
};

api.salesforceCheckLeadExists = async (email) => {
  try {
    logger.log('API: Checking if Lead exists by email:', email);
    const response = await api.post('/api/salesforce/check-lead-exists', {
      email: email
    });
    logger.log('API: Salesforce check Lead exists response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Salesforce Check Lead Exists API Error:', error);
    throw error;
  }
};

api.salesforceCheckLeadsExist = async (emails) => {
  try {
    logger.log('API: Checking if Leads exist by emails:', emails);
    const response = await api.post('/api/salesforce/check-lead-exists', {
      emails: emails
    });
    logger.log('API: Salesforce check Leads exist response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Salesforce Check Leads Exist API Error:', error);
    throw error;
  }
};

// HubSpot Integration APIs
api.hubspotConnect = async (accessToken) => {
  try {
    logger.log('API: Connecting to HubSpot');
    const response = await api.post('/api/hubspot/connect', {
      access_token: accessToken
    });
    logger.log('API: HubSpot connect response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('HubSpot Connect API Error:', error);
    throw error;
  }
};

api.hubspotDisconnect = async () => {
  try {
    logger.log('API: Disconnecting from HubSpot');
    const response = await api.post('/api/hubspot/disconnect');
    logger.log('API: HubSpot disconnect response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('HubSpot Disconnect API Error:', error);
    throw error;
  }
};

api.hubspotStatus = async () => {
  try {
    logger.log('API: Checking HubSpot connection status');
    const response = await api.get('/api/hubspot/status');
    logger.log('API: HubSpot status response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('HubSpot Status API Error:', error);
    throw error;
  }
};

api.hubspotCreateContact = async (contactData, checkDuplicates = true) => {
  try {
    logger.log('API: Creating new HubSpot Contact:', contactData);
    const response = await api.post('/api/hubspot/create-contact', {
      contact_data: contactData,
      check_duplicates: checkDuplicates
    });
    logger.log('API: HubSpot create Contact response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('HubSpot Create Contact API Error:', error);
    throw error;
  }
};

api.hubspotCheckContactExists = async (email) => {
  try {
    logger.log('API: Checking if Contact exists by email:', email);
    const response = await api.post('/api/hubspot/check-contact-exists', {
      email: email
    });
    logger.log('API: HubSpot check Contact exists response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('HubSpot Check Contact Exists API Error:', error);
    throw error;
  }
};

api.hubspotCheckContactsExist = async (emails) => {
  try {
    logger.log('API: Checking if Contacts exist by emails:', emails);
    const response = await api.post('/api/hubspot/check-contact-exists', {
      emails: emails
    });
    logger.log('API: HubSpot check Contacts exist response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('HubSpot Check Contacts Exist API Error:', error);
    throw error;
  }
};

api.hubspotGetRecords = async (limit = 100, offset = 0, filters = {}, search = '', sortField = 'hs_lastmodifieddate', sortOrder = 'DESC') => {
  try {
    logger.log('API: Fetching HubSpot contacts:', { limit, offset, filters, search, sortField, sortOrder });
    const params = {
      limit,
      offset,
      sort_field: sortField,
      sort_order: sortOrder
    };
    
    if (search) {
      params.search = search;
    }
    
    if (filters.company) {
      params.company = filters.company;
    }
    
    if (filters.no_email) {
      params.no_email = 'true';
    }
    
    if (filters.no_phone) {
      params.no_phone = 'true';
    }
    
    if (filters.lifecycle_status) {
      params.lifecycle_status = filters.lifecycle_status;
    }
    
    const response = await api.get('/api/hubspot/records', { params });
    logger.log('API: HubSpot get records response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('HubSpot Get Records API Error:', error);
    throw error;
  }
};

api.hubspotEnrich = async (contactIds) => {
  try {
    logger.log(`API: Enriching HubSpot contacts:`, contactIds);
    const response = await api.post('/api/hubspot/enrich', {
      contact_ids: contactIds
    });
    logger.log('API: HubSpot enrich response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('HubSpot Enrich API Error:', error);
    throw error;
  }
};

api.hubspotBulkEnrich = async (contactIds) => {
  try {
    logger.log(`API: Bulk enriching ${contactIds.length} HubSpot contacts`);
    const response = await api.post('/api/hubspot/enrich', {
      contact_ids: contactIds
    });
    logger.log('API: HubSpot bulk enrich response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('HubSpot Bulk Enrich API Error:', error);
    throw error;
  }
};

api.hubspotSync = async (contactId, fields) => {
  try {
    logger.log(`API: Syncing enriched data to HubSpot contact ${contactId}`);
    const response = await api.post('/api/hubspot/sync', {
      contact_id: contactId,
      fields: fields
    });
    logger.log('API: HubSpot sync response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('HubSpot Sync API Error:', error);
    throw error;
  }
};

// Subscription Add-Ons APIs
api.getAddOns = async () => {
  try {
    logger.log('API: Getting subscription add-ons');
    const response = await api.get('/api/subscription/addons');
    logger.log('API: Get add-ons response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Get Add-Ons API Error:', error);
    throw error;
  }
};

api.activateAddOn = async (addonId, paymentMethod = 'manual', paymentToken = null) => {
  try {
    logger.log(`API: Activating add-on ${addonId}`);
    const response = await api.post('/api/subscription/addons/activate', {
      addon_id: addonId,
      payment_method: paymentMethod,
      payment_token: paymentToken
    });
    logger.log('API: Activate add-on response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Activate Add-On API Error:', error);
    throw error;
  }
};

api.cancelAddOn = async (addonId) => {
  try {
    logger.log(`API: Cancelling add-on ${addonId}`);
    const response = await api.post('/api/subscription/addons/cancel', {
      addon_id: addonId
    });
    logger.log('API: Cancel add-on response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Cancel Add-On API Error:', error);
    throw error;
  }
};

api.getAddOnStatus = async () => {
  try {
    logger.log('API: Getting add-on status');
    const response = await api.get('/api/subscription/addons/status');
    logger.log('API: Get add-on status response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Get Add-On Status API Error:', error);
    throw error;
  }
};

api.checkAddOnAccess = async (addonCode) => {
  try {
    logger.log(`API: Checking add-on access for ${addonCode}`);
    const response = await api.get(`/api/subscription/addons/check/${addonCode}`);
    logger.log('API: Check add-on access response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Check Add-On Access API Error:', error);
    throw error;
  }
};

// Admin email management
api.adminEmailSearchUsers = async (query, limit = 50) => {
  try {
    const response = await api.get('/api/admin/email/users/search', {
      params: { q: query, limit },
    });
    return response.data;
  } catch (error) {
    logger.error('Admin email user search error:', error);
    throw error;
  }
};

api.adminEmailGetInactiveUsers = async (days = 30, limit = 200) => {
  try {
    const response = await api.get('/api/admin/email/inactive-users', {
      params: { days, limit },
    });
    return response.data;
  } catch (error) {
    logger.error('Admin email inactive users error:', error);
    throw error;
  }
};

api.adminEmailPreview = async (templateName, templateData) => {
  try {
    const response = await api.post('/api/admin/email/preview', {
      template_name: templateName,
      template_data: templateData,
    });
    return response.data;
  } catch (error) {
    logger.error('Admin email preview error:', error);
    throw error;
  }
};

api.adminEmailSendBulk = async (payload) => {
  try {
    const response = await api.post('/api/admin/email/send-bulk', payload);
    return response.data;
  } catch (error) {
    logger.error('Admin email send bulk error:', error);
    throw error;
  }
};

api.adminEmailGetPresets = async () => {
  try {
    const response = await api.get('/api/admin/email/presets');
    return response.data;
  } catch (error) {
    logger.error('Admin email get presets error:', error);
    throw error;
  }
};

api.adminEmailCreatePreset = async (data) => {
  try {
    const response = await api.post('/api/admin/email/presets', data);
    return response.data;
  } catch (error) {
    logger.error('Admin email create preset error:', error);
    throw error;
  }
};

api.adminEmailDeletePreset = async (presetId) => {
  try {
    const response = await api.delete(`/api/admin/email/presets/${presetId}`);
    return response.data;
  } catch (error) {
    logger.error('Admin email delete preset error:', error);
    throw error;
  }
};

api.adminEmailUsePreset = async (presetId) => {
  try {
    const response = await api.post(`/api/admin/email/presets/${presetId}/use`);
    return response.data;
  } catch (error) {
    logger.error('Admin email use preset error:', error);
    throw error;
  }
};

api.adminEmailGetAuthStatus = async () => {
  try {
    const response = await api.get('/api/admin/email/status');
    return response.data;
  } catch (error) {
    logger.error('Admin email status error:', error);
    throw error;
  }
};

api.forgotPassword = async (email) => {
  try {
    const response = await api.post('/api/auth/forgot-password', { email });
    return response.data;
  } catch (error) {
    logger.error('Forgot password API error:', error);
    throw error;
  }
};

api.validateResetToken = async (token) => {
  try {
    const response = await api.get('/api/auth/reset-password/validate', {
      params: { token },
    });
    return response.data;
  } catch (error) {
    logger.error('Validate reset token API error:', error);
    throw error;
  }
};

api.resetPassword = async (token, password) => {
  try {
    const response = await api.post('/api/auth/reset-password', { token, password });
    return response.data;
  } catch (error) {
    logger.error('Reset password API error:', error);
    throw error;
  }
};

api.getSearchHistory = async () => {
  try {
    const response = await api.get('/api/search-history');
    return response.data;
  } catch (error) {
    logger.error('Get search history API error:', error);
    throw error;
  }
};

api.addDomainSearchHistory = async (record) => {
  try {
    const response = await api.post('/api/search-history/domain', { record });
    return response.data;
  } catch (error) {
    logger.error('Add domain search history API error:', error);
    throw error;
  }
};

api.addContactSearchHistory = async (responseData) => {
  try {
    const response = await api.post('/api/search-history/contact', { response: responseData });
    return response.data;
  } catch (error) {
    logger.error('Add contact search history API error:', error);
    throw error;
  }
};

api.deleteSearchHistoryEntry = async (entryId) => {
  try {
    const response = await api.delete(`/api/search-history/${entryId}`);
    return response.data;
  } catch (error) {
    logger.error('Delete search history entry API error:', error);
    throw error;
  }
};

api.clearSearchHistory = async (type) => {
  try {
    const response = await api.delete('/api/search-history', { params: { type } });
    return response.data;
  } catch (error) {
    logger.error('Clear search history API error:', error);
    throw error;
  }
};

export default api; 